import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Rating from "@mui/material/Rating";
import { SecureImage } from "./SecureImage";
import ZoomableImage from "./ZoomableImage";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const SetCard = ({
  stock = {},
  addToCart,
  removeFromCart,
  cart = [],
  setLoginPopup,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // SAFE parent + subs
  const parent =
    { ...stock?.parentItem, out_of_stock: stock?.out_of_stock } || {};
  const subs = Array.isArray(stock?.subItems) ? stock.subItems : [];

  const allItems = [parent, ...subs].filter(Boolean);

  const [selected, setSelected] = useState(parent);
  const [dialogIndex, setDialogIndex] = useState(-1);
  const [rating, setRating] = useState(0);

  const handleRatingChange = (e, newValue) => setRating(newValue);

  // SAFE index
  const openDialog = () => {
    const index = allItems.findIndex((i) => i?.tagno === selected?.tagno);
    setDialogIndex(index >= 0 ? index : 0);
  };

  const closeDialog = () => {
    setDialogIndex(-1);
    setRating(0);
  };

  const currentItem = dialogIndex >= 0 ? allItems[dialogIndex] || {} : {};

  return (
    <>
      {/* CARD */}
      <Box
        sx={{
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          bgcolor: "#fff",
          position: "relative",
        }}
      >
        {/* Out Of Stock Badge */}
        {stock?.out_of_stock && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 0,
              bgcolor: "#d32f2f",
              color: "white",
              px: 1.4,
              py: 0.4,
              fontSize: "0.7rem",
              fontWeight: 700,
              zIndex: 10,
              clipPath:
                "polygon(0 0, 100% 0, calc(100% - 12px) 50%, 100% 100%, 0 100%)",
              borderRadius: "0 4px 4px 0",
            }}
          >
            OUT OF STOCK
          </Box>
        )}

        {/* IMAGE SECTION */}
        <Box
          sx={{
            width: "100%",
            height: 225,
            display: "flex",
            justifyContent: "space-between",
            p: 1,
            pt: 1.2,
            pb: 1,
          }}
        >
          {/* Children Items */}
          <Box
            sx={{
              width: "26%",
              height: 207,
              display: "flex",
              flexDirection: "column",
              gap: 0.7,
              overflowY: allItems.length > 3 ? "auto" : "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {allItems.slice(1).map((item) => (
              <Box
                key={item?.tagno}
                onClick={openDialog}
                sx={{
                  height: 65,
                  minHeight: 65,
                  maxHeight: 65,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                <SecureImage
                  item={item}
                  width={"100%"}
                  height={"100%"}
                  borderRadius={2}
                />
              </Box>
            ))}
          </Box>

          {/* Parent Item */}
          <Box
            sx={{
              width: "70%",
              height: "100%",
              borderRadius: 1.5,
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={openDialog}
          >
            <SecureImage
              item={selected}
              width={"100%"}
              height={"100%"}
              borderRadius={2}
            />
          </Box>
        </Box>

        {/* TEXT + CART */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 1,
            pt: 0.5,
            alignItems: "center",
            bgcolor: "#ffbf001d",
          }}
        >
          <Box sx={{ width: "75%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {parent?.design_name} (Set)
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: "bold" }}>
              Total Gross: {stock?.totalGrossWeight}g
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: "bold" }}>
              Total Net: {stock?.totalNetWeight}g
            </Typography>
            {/* <Typography
              sx={{ fontSize: 13, fontWeight: "bold", color: "#ba3800" }}
            >
              #{selected?.tagno}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {selected?.design_name}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              Gross Wt: {parseFloat(selected?.gross).toFixed(3)} gm
            </Typography> */}
          </Box>

          {stock?.out_of_stock ? (
            <Button
              sx={{
                padding: 0,
                width: 110,
                height: 34,
                borderRadius: "5px",
                backgroundColor: "#d0820cff",
                color: "white",
                "&:hover": { backgroundColor: "#ac6904ff" },
                fontSize: "0.62rem",
                fontWeight: 600,
              }}
              onClick={() => {
                // If user not logged in → trigger login screen
                if (!addToCart || !removeFromCart) {
                  setLoginPopup?.(true);
                  return;
                }

                // Otherwise normal cart logic
                cart.some((i) => i?.tagno === selected?.tagno)
                  ? removeFromCart(selected)
                  : addToCart(stock);
              }}
            >
              {cart.some((i) => i?.tagno === selected?.tagno)
                ? "Discard Order"
                : "Place Order"}
            </Button>
          ) : (
            <Button
              variant={
                cart.some((i) => i?.tagno === selected?.tagno)
                  ? ""
                  : "contained"
              }
              size="small"
              onClick={() => {
                // If user not logged in → trigger login screen
                if (!addToCart || !removeFromCart) {
                  setLoginPopup?.(true);
                  return;
                }

                // Otherwise normal cart logic
                cart.some((i) => i?.tagno === selected?.tagno)
                  ? removeFromCart(selected)
                  : addToCart(stock);
              }}
              sx={{
                minWidth: 46,
                width: 46,
                height: 46,
                border: `1px solid #995c004e`,
                borderRadius: "50%",
                backgroundColor: !cart.some((i) => i?.tagno === selected?.tagno)
                  ? "#FFF2DB"
                  : "transparent",
                "&:hover": {
                  backgroundColor: !cart.some(
                    (i) => i?.tagno === selected?.tagno,
                  )
                    ? "#ffe0b3"
                    : "rgba(0,0,0,0.05)",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
              }}
            >
              {cart.some((i) => i?.tagno === selected?.tagno) ? (
                <RemoveShoppingCartIcon
                  sx={{ fontSize: 20, color: "#a33c00ff" }}
                />
              ) : (
                <AddShoppingCartIcon
                  sx={{ fontSize: 20, color: "#a33c00ff" }}
                />
              )}
            </Button>
          )}
        </Box>
      </Box>

      {/* DIALOG */}
      <Dialog
        open={dialogIndex >= 0}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          {/* Close Icon */}
          <Box
            sx={{
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "#696969",
              bgcolor: "#ffd580",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 10,
            }}
            onClick={closeDialog}
          >
            <CloseRoundedIcon sx={{ fill: "#5a3c00", fontSize: 22 }} />
          </Box>

          {/* OUT OF STOCK */}
          {currentItem?.out_of_stock && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 0,
                background: "#d32f2f",
                color: "white",
                px: 2,
                py: 0.6,
                fontSize: "0.75rem",
                fontWeight: "bold",
                clipPath: "polygon(0 0, 100% 0, 85% 100%, 0 100%)",
                zIndex: 50,
              }}
            >
              OUT OF STOCK
            </Box>
          )}

          {/* Move to Prev Item Icon */}
          {allItems.length > 1 && (
            <ChevronLeftIcon
              onClick={() =>
                setDialogIndex((prev) =>
                  prev === 0 ? allItems.length - 1 : prev - 1,
                )
              }
              sx={{
                fontSize: 36,
                borderRadius: 100,
                position: "absolute",
                left: 5,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(58, 58, 58, 0.5)",
                zIndex: 10,
                "&:hover": {
                  background: "rgba(58, 58, 58,0.75)",
                },
                fill: "#FFF",
                cursor: "pointer",
              }}
            />
          )}

          {/* Move to Next Item Icon */}
          {allItems.length > 1 && (
            <ChevronRightIcon
              onClick={() =>
                setDialogIndex((prev) =>
                  prev === allItems.length - 1 ? 0 : prev + 1,
                )
              }
              sx={{
                fontSize: 36,
                borderRadius: 100,
                position: "absolute",
                right: 5,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(58, 58, 58, 0.5)",
                zIndex: 10,
                "&:hover": {
                  background: "rgba(58, 58, 58,0.75)",
                },
                fill: "#FFF",
                cursor: "pointer",
              }}
            />
          )}

          {/* Items Count */}
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              right: 20,
              background: "rgba(0,0,0,0.65)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "18px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {dialogIndex + 1} / {allItems.length}
          </Box>

          {/* Current Item Details */}
          {currentItem && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: isMobile ? "column" : "row",
                gap: 3,
              }}
            >
              {/* Image */}
              <Box
                sx={{
                  flex: 3,
                  height: "65vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <ZoomableImage>
                  <SecureImage
                    item={currentItem}
                    width={"100%"}
                    height={"100%"}
                    borderRadius={2}
                  />
                </ZoomableImage>
              </Box>

              {/* Details */}
              <Box
                sx={{
                  flex: 2,
                  textAlign: "left",
                  position: "relative",
                  width: "100%",
                }}
              >
                {/* Set Name */}
                <Typography
                  sx={{ fontSize: 16, fontWeight: 600, mb: isMobile ? 1.5 : 3 }}
                >
                  {parent?.itemtype_name} ({parent?.design_name}) Set
                </Typography>

                {/* Item Type Name */}
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, mb: isMobile ? 0.5 : 2 }}
                >
                  {currentItem?.itemtype_name} ({currentItem?.design_name})
                </Typography>

                {/* Row 1: Tag No + Pcs */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: isMobile ? "row" : "column",
                    mb: isMobile ? 0.5 : 0,
                  }}
                >
                  <Typography sx={{ fontSize: 15, color: "#333" }}>
                    Tag No:{" "}
                    <b style={{ color: "#222" }}>{currentItem?.tagno}</b>
                  </Typography>

                  <Typography sx={{ fontSize: 15, color: "#333" }}>
                    Pcs: <b style={{ color: "#222" }}>{currentItem?.pcs}</b>
                  </Typography>
                </Box>

                {/* Row 2: Gross + Net */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: isMobile ? "row" : "column",
                    mb: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: 15, color: "#333" }}>
                    Gross:{" "}
                    <b style={{ color: "#222" }}>
                      {parseFloat(currentItem?.gross || 0).toFixed(3)}g
                    </b>
                  </Typography>

                  <Typography sx={{ fontSize: 15, color: "#333" }}>
                    Net:{" "}
                    <b style={{ color: "#222" }}>
                      {parseFloat(currentItem?.netwt || 0).toFixed(3)}g
                    </b>
                  </Typography>
                </Box>

                {/* Rating */}
                {/* <Box mt={2}>
                  <Typography variant="subtitle1">Rate this item:</Typography>
                  <Rating
                    name="rating"
                    value={rating}
                    onChange={handleRatingChange}
                    size="small"
                  />
                </Box> */}

                {/* Add To Cart / Place Order Button */}
                <Box sx={{ mt: 2 }}>
                  {currentItem?.out_of_stock ? (
                    <Button
                      sx={{
                        fontSize: "11px",
                        padding: "5px 14px",
                        minWidth: "100px",
                        borderRadius: "20px",
                        fontWeight: 600,
                        background: "#d0820cff",
                        color: "white",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                      }}
                      onClick={() => console.log("Place order for OOS item")}
                    >
                      Place Order
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        // If user not logged in → trigger login screen
                        if (!addToCart || !removeFromCart) {
                          setLoginPopup?.(true);
                          return;
                        }

                        // Otherwise normal cart logic
                        cart.some((i) => i?.tagno === selected?.tagno)
                          ? removeFromCart(selected)
                          : addToCart(stock);
                      }}
                      sx={{
                        fontSize: "11px",
                        padding: "5px 14px",
                        minWidth: "80px",
                        borderRadius: "20px",
                        fontWeight: 600,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        background: cart.some(
                          (c) =>
                            String(c?.tagno) === String(currentItem?.tagno),
                        )
                          ? "#ffb3b3"
                          : "#ffd580",
                        color: "#5a3c00",
                      }}
                    >
                      {cart.some(
                        (c) => String(c?.tagno) === String(currentItem?.tagno),
                      )
                        ? "Remove from cart"
                        : "Add to cart"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SetCard;
